import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { TrendingUp, TrendingDown, Minus, Award, Users, UserCheck } from 'lucide-react';
import { useTranslation } from '../translations';

/**
 * EvolutionChart - Gráfico Evolutivo de Desempenho
 * 
 * Componente que exibe a evolução do desempenho do trabalhador ao longo do tempo.
 * 
 * Tripé:
 * - Justiça: Mostra separação entre avaliações técnicas e de empregadores
 * - Segurança: Dados do próprio trabalhador ou públicos (perfil)
 * - Simplicidade: Visualização clara e intuitiva
 */
const EvolutionChart = ({ workerId, showTitle = true, compact = false }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvolutionData = async () => {
      if (!workerId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/evaluations/users/${workerId}/evolution?months=12`
        );
        
        if (!response.ok) {
          throw new Error('Erro ao carregar dados');
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error('Erro ao buscar evolução:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvolutionData();
  }, [workerId]);

  // Renderizar selo
  const renderSealBadge = (seal) => {
    const sealConfig = {
      gold: { emoji: '🥇', label: t('sealGold') || 'Ouro', color: 'bg-yellow-500' },
      silver: { emoji: '🥈', label: t('sealSilver') || 'Prata', color: 'bg-gray-400' },
      bronze: { emoji: '🥉', label: t('sealBronze') || 'Bronze', color: 'bg-orange-600' },
      none: { emoji: '⭕', label: t('sealNone') || 'Sem selo', color: 'bg-gray-300' }
    };
    
    const config = sealConfig[seal] || sealConfig.none;
    
    return (
      <Badge className={`${config.color} text-white`}>
        {config.emoji} {config.label}
      </Badge>
    );
  };

  // Renderizar ícone de tendência
  const renderTrendIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      default:
        return <Minus className="w-5 h-5 text-gray-500" />;
    }
  };

  // Tooltip customizado
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 dark:text-white">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: <span className="font-medium">{entry.value} pts</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card className={compact ? "p-4" : ""}>
        <CardContent className="flex items-center justify-center h-48">
          <div className="animate-pulse text-gray-500">
            {t('loading') || 'Carregando...'}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={compact ? "p-4" : ""}>
        <CardContent className="flex items-center justify-center h-48 text-red-500">
          {error}
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.chart_data || data.chart_data.length === 0) {
    return (
      <Card className={compact ? "p-4" : ""}>
        <CardContent className="flex flex-col items-center justify-center h-48 text-gray-500">
          <Award className="w-12 h-12 mb-2 opacity-50" />
          <p>{t('noEvaluationsYet') || 'Ainda sem avaliações'}</p>
          <p className="text-sm">{t('firstEvaluationMessage') || 'Os dados aparecerão após a primeira avaliação'}</p>
        </CardContent>
      </Card>
    );
  }

  const { chart_data, summary } = data;

  return (
    <Card className={compact ? "p-2" : ""}>
      {showTitle && (
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="w-5 h-5 text-green-600" />
            {t('performanceEvolution') || 'Evolução do Desempenho'}
            <span className="text-sm font-normal text-gray-500">
              ({t('last12Months') || 'Últimos 12 meses'})
            </span>
          </CardTitle>
        </CardHeader>
      )}
      
      <CardContent>
        {/* Resumo */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {/* Selo Atual */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">{t('currentSeal') || 'Selo Atual'}</p>
              <div className="flex justify-center">
                {renderSealBadge(summary.current_seal)}
              </div>
              <p className="text-lg font-bold mt-1">{summary.current_score} pts</p>
            </div>
            
            {/* Tendência */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">{t('trend') || 'Tendência'}</p>
              <div className="flex items-center justify-center gap-1">
                {renderTrendIcon(summary.trend_direction)}
                <span className={`font-bold ${
                  summary.trend_direction === 'up' ? 'text-green-600' : 
                  summary.trend_direction === 'down' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {summary.trend} pts
                </span>
              </div>
              <p className="text-xs text-gray-500">{t('lastThreeMonths') || 'Últimos 3 meses'}</p>
            </div>
            
            {/* Avaliações Qualificadas */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                <UserCheck className="w-3 h-3 inline mr-1" />
                {t('qualifiedEvaluations') || 'Qualificadas'}
              </p>
              <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                {summary.technical_count}
              </p>
              <p className="text-xs text-blue-500">{t('weight3x') || 'Peso 3x'}</p>
            </div>
            
            {/* Avaliações Padrão */}
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 text-center">
              <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">
                <Users className="w-3 h-3 inline mr-1" />
                {t('standardEvaluations') || 'Padrao'}
              </p>
              <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
                {summary.employer_count}
              </p>
              <p className="text-xs text-purple-500">{t('weight1x') || 'Peso 1x'}</p>
            </div>
          </div>
        )}

        {/* Gráfico */}
        <div className={compact ? "h-48" : "h-64"}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chart_data}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={[0, 100]} 
                tick={{ fontSize: 11 }}
                width={35}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                formatter={(value) => {
                  const labels = {
                    score: t('weightedScore') || 'Pontuacao Ponderada',
                    technical: t('qualifiedEvaluations') || 'Qualificadas (peso 3x)',
                    employer: t('standardEvaluations') || 'Padrao (peso 1x)'
                  };
                  return labels[value] || value;
                }}
              />
              
              {/* Linhas de referência dos selos */}
              <ReferenceLine y={90} stroke="#FFD700" strokeDasharray="5 5" label={{ value: '🥇', position: 'right', fontSize: 10 }} />
              <ReferenceLine y={75} stroke="#C0C0C0" strokeDasharray="5 5" label={{ value: '🥈', position: 'right', fontSize: 10 }} />
              <ReferenceLine y={60} stroke="#CD7F32" strokeDasharray="5 5" label={{ value: '🥉', position: 'right', fontSize: 10 }} />
              
              {/* Linha principal - Pontuação Ponderada */}
              <Line
                type="monotone"
                dataKey="score"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6 }}
                name="score"
              />
              
              {/* Linha de avaliações técnicas */}
              <Line
                type="monotone"
                dataKey="technical"
                stroke="#3B82F6"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#3B82F6', strokeWidth: 1, r: 3 }}
                name="technical"
                connectNulls
              />
              
              {/* Linha de avaliações de empregadores */}
              <Line
                type="monotone"
                dataKey="employer"
                stroke="#8B5CF6"
                strokeWidth={2}
                strokeDasharray="3 3"
                dot={{ fill: '#8B5CF6', strokeWidth: 1, r: 3 }}
                name="employer"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Legenda de cores dos selos */}
        <div className="flex justify-center gap-4 mt-3 text-xs text-gray-500">
          <span>🥇 ≥90 pts</span>
          <span>🥈 ≥75 pts</span>
          <span>🥉 ≥60 pts</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default EvolutionChart;
